import { useCallback, useEffect, useRef, type PointerEvent, type ReactNode } from 'react';

interface Props {
  label: string;
  className?: string;
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}

/**
 * Bottom sheet on a native modal <dialog>: focus is trapped, Esc and a tap on
 * the scrim close it. It slides up on entry (@starting-style); the exit runs
 * through `data-closing` so it animates in every browser before unmounting.
 */
export function Sheet({ label, className = '', onClose, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const closing = useRef(false);
  const drag = useRef<{
    pointerId: number;
    startY: number;
    moved: boolean;
    samples: { y: number; time: number }[];
  } | null>(null);
  const suppressClick = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const close = useCallback(async () => {
    const dialog = ref.current;
    if (!dialog || closing.current) return;
    closing.current = true;
    drag.current = null;
    dialog.removeAttribute('data-dragging');
    dialog.setAttribute('data-closing', '');
    const animations = dialog.getAnimations({ subtree: false });
    if (animations.length > 0) {
      await Promise.race([
        Promise.allSettled(animations.map((a) => a.finished)),
        new Promise((r) => setTimeout(r, 400)),
      ]);
    }
    dialog.close();
    onCloseRef.current();
  }, []);

  const resetDrag = () => {
    drag.current = null;
    ref.current?.removeAttribute('data-dragging');
    ref.current?.style.removeProperty('--sheet-drag-y');
  };

  const moveDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current;
    const dialog = ref.current;
    if (!current || current.pointerId !== event.pointerId || !dialog) return;
    const distance = Math.max(0, event.clientY - current.startY);
    if (Math.abs(event.clientY - current.startY) > 5) current.moved = true;
    current.samples.push({ y: event.clientY, time: event.timeStamp });
    current.samples = current.samples.filter((sample) => event.timeStamp - sample.time <= 100);
    if (current.moved) {
      suppressClick.current = true;
      dialog.setAttribute('data-dragging', '');
      // Keep transform in CSS so data-closing can animate from this position.
      dialog.style.setProperty('--sheet-drag-y', `${distance}px`);
    }
  };

  const endDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current;
    const dialog = ref.current;
    if (!current || current.pointerId !== event.pointerId || !dialog) return;
    moveDrag(event);
    const distance = Math.max(0, event.clientY - current.startY);
    const first = current.samples[0];
    const elapsed = event.timeStamp - first.time;
    const velocity = elapsed > 0 ? (event.clientY - first.y) / elapsed : 0;
    // A deliberate pull or a short, quick downward flick dismisses the sheet.
    const threshold = Math.min(140, Math.max(64, dialog.offsetHeight * 0.3));
    const dismiss = current.moved && (distance >= threshold || (distance >= 24 && velocity > 0.6));
    if (dismiss) void close();
    else resetDrag();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      dialog.showModal();
      // Start on a field marked data-autofocus, else on the sheet itself
      // rather than on the first chip inside it.
      (dialog.querySelector<HTMLElement>('[data-autofocus]') ?? dialog).focus();
    }

    const onCancel = (event: Event) => {
      event.preventDefault();
      void close();
    };
    // Native closedby="any" can close without our animated close callback.
    const onNativeClose = () => {
      if (closing.current) return;
      closing.current = true;
      onCloseRef.current();
    };
    // Light dismiss for browsers without <dialog closedby> (Safari).
    const onClick = (event: MouseEvent) => {
      if ('closedBy' in HTMLDialogElement.prototype || event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      const inside = r.top <= event.clientY && event.clientY <= r.bottom && r.left <= event.clientX && event.clientX <= r.right;
      if (!inside) void close();
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onNativeClose);
    dialog.addEventListener('click', onClick);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onNativeClose);
      dialog.removeEventListener('click', onClick);
    };
  }, [close]);

  return (
    <dialog ref={ref} className={`sheet ${className}`} aria-label={label} tabIndex={-1} {...{ closedby: 'any' }}>
      <button
        type="button"
        className="sheet__grabber"
        aria-label="Sluiten (of sleep omlaag)"
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0 || closing.current || drag.current) return;
          suppressClick.current = false;
          drag.current = {
            pointerId: event.pointerId,
            startY: event.clientY,
            moved: false,
            samples: [{ y: event.clientY, time: event.timeStamp }],
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={(event) => {
          if (drag.current?.pointerId === event.pointerId) resetDrag();
        }}
        onLostPointerCapture={(event) => {
          if (drag.current?.pointerId === event.pointerId) resetDrag();
        }}
        onClick={(event) => {
          if (event.detail !== 0 && suppressClick.current) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          void close();
        }}
      />
      {children(() => void close())}
    </dialog>
  );
}
